import React, { useEffect, useRef, useState } from "react";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { FiBell, FiCheckCircle } from "react-icons/fi";
import { formatTimeAgo } from "../../utils/helper";

const Notification = ({
  notifications,
  onNotificationClick,
  onClearAll,
  getNotifications,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localNotes, setLocalNotes] = useState([]);

  const dropdownRef = useRef();

  useEffect(() => {
    setLocalNotes(notifications);
  }, [notifications]);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

const handleNotificationClick = async (uuid, isRead) => {
  if (isRead) return;

  const res = await onNotificationClick(uuid);
  if (res?.errorCode === 0) {
    setLocalNotes((prev) =>
      prev.map((n) =>
        n.notificationUuid === uuid ? { ...n, isRead: true } : n
      )
    );
  }
};

const handleClearAll = async () => {
  const res = await onClearAll(); 
  if (res?.errorCode === 0) {
    setLocalNotes((prev) => prev.map((n) => ({ ...n, isRead: true })));
  }
};

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEsc = (e) => e.key === "Escape" && setIsOpen(false);
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  return (
    <div className="relative mx-3" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="relative hover:text-gray-700 focus:outline-none my-2"
      >
        <FiBell className="text-lg sm:text-xl md:text-2xl" />
        {localNotes.filter((n) => !n.isRead).length > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full">
            {localNotes.filter((n) => !n.isRead).length}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-40 sm:hidden z-40" />

          <div
            className={`fixed top-0 right-0 h-full w-80 max-w-[90vw] bg-white shadow-lg z-50 
              transform transition-all duration-300 ease-in-out 
              sm:absolute sm:top-auto sm:right-0 sm:mt-2 sm:w-80 sm:rounded-lg sm:h-auto 
              ${
                isOpen
                  ? "translate-x-0 opacity-100"
                  : "translate-x-full opacity-0"
              }`}
          >
            <div className="flex justify-between items-center p-3 border-b font-semibold text-gray-800">
              <span>Notifications</span>
              <div className="flex items-center gap-2">
                {localNotes.some((n) => !n.isRead) && (
                  <button
                    onClick={handleClearAll}
                    className="text-xs text-gray-700 hover:underline"
                  >
                    Mark all as read
                  </button>
                )}
                <button
                  className="sm:hidden text-gray-600 hover:text-black"
                  onClick={() => setIsOpen(false)}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* List */}
            <div className="divide-y max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  Loading...
                </div>
              ) : localNotes.length > 0 ? (
                localNotes.map((note, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-2 py-2 px-3 hover:bg-gray-50 transition cursor-pointer ${
                      note.isRead ? "opacity-50" : ""
                    }`}            
                    onClick={() =>
                      handleNotificationClick(
                        note.notificationUuid,
                        note.isRead
                      )
                    }
                  >
                    <div className="pt-1 flex-shrink-0">
                      {note.type === "payment" ? (
                        <FiCheckCircle className="text-green-600 text-lg" />
                      ) : note.type === "course" ? (
                        <AiOutlineCheckCircle className="text-indigo-600 text-lg" />
                      ) : (
                        <FiBell className="text-gray-700 text-lg" />
                      )}
                    </div>
                    <div className="text-xs sm:text-sm flex-1">
                      <p
                        className={`font-semibold ${
                          note.isRead ? "text-gray-400" : ""
                        }`}
                      >
                        {note.title}
                      </p>
                      <p
                        className={`leading-snug ${
                          note.isRead ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {note.body}
                      </p>
                      <span className="text-gray-400 text-[10px] sm:text-xs">
                        {formatTimeAgo(note.createdAt)}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No new notifications
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Notification;
